import { useEffect, useRef, useState, type ReactElement } from "react";

export type DateRangePickerProps = {
  from: string | null;
  to: string | null;
  /** Today as YYYY-MM-DD — future days are disabled. */
  todayIso: string;
  /** A resolved Intl tag (e.g. "ar-EG", "en-US"), not a bare portal locale. */
  locale: string;
  /** Text on the trigger button (the current range, or a prompt). */
  triggerLabel: string;
  /** Whether a custom range is active (drives the accent outline). */
  active: boolean;
  /** Copy lives in the portals — this package ships no strings. */
  applyLabel: string;
  clearLabel: string;
  onApply: (from: string, to: string) => void;
  onClear: () => void;
};

const pad = (n: number): string => String(n).padStart(2, "0");
const iso = (y: number, m: number, d: number): string =>
  `${y}-${pad(m + 1)}-${pad(d)}`;

/** Seven localized short weekday labels, Sunday-first (matches getUTCDay). */
function weekdayLabels(locale: string): string[] {
  const fmt = new Intl.DateTimeFormat(locale, { weekday: "short" });
  // 2023-01-01 is a Sunday (UTC).
  return Array.from({ length: 7 }, (_, i) =>
    fmt.format(new Date(Date.UTC(2023, 0, 1 + i))),
  );
}

function MonthGrid({
  year,
  month,
  start,
  end,
  todayIso,
  onPick,
  locale,
}: {
  year: number;
  month: number;
  start: string | null;
  end: string | null;
  todayIso: string;
  onPick: (day: string) => void;
  locale: string;
}): ReactElement {
  const first = new Date(Date.UTC(year, month, 1));
  const lead = first.getUTCDay();
  const days = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const title = first.toLocaleDateString(locale, {
    month: "long",
    year: "numeric",
  });
  const cells: (number | null)[] = [
    ...Array.from({ length: lead }, () => null),
    ...Array.from({ length: days }, (_, i) => i + 1),
  ];
  return (
    <div className="flex flex-col gap-2">
      <div
        className="text-center text-[13px] font-bold"
        style={{ color: "var(--text-primary)" }}
      >
        {title}
      </div>
      <div
        className="grid grid-cols-7 gap-1 text-center text-[10.5px]"
        style={{ color: "var(--text-muted)" }}
      >
        {weekdayLabels(locale).map((w, i) => (
          <span key={i}>{w}</span>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((day, idx) => {
          if (day === null) {
            return <span key={`b${idx}`} />;
          }
          const value = iso(year, month, day);
          const inRange = Boolean(
            start && end && value >= start && value <= end,
          );
          const isEdge = value === start || value === end;
          const isToday = value === todayIso;
          const disabled = value > todayIso;
          return (
            <button
              key={value}
              type="button"
              disabled={disabled}
              onClick={() => onPick(value)}
              className="flex h-8 items-center justify-center rounded-[8px] text-[12px]"
              style={{
                background: isEdge
                  ? "var(--accent-500)"
                  : inRange
                    ? "var(--soft2)"
                    : "transparent",
                color: isEdge
                  ? "var(--on-accent)"
                  : disabled
                    ? "var(--text-muted)"
                    : "var(--text-primary)",
                opacity: disabled ? 0.4 : 1,
                fontWeight: isEdge || isToday ? 700 : 500,
                outline:
                  isToday && !isEdge ? "1px solid var(--card-br)" : "none",
              }}
            >
              {day}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * A two-month calendar range picker: pick a start then an end day, see the span
 * highlighted, and Apply. Styled entirely from design tokens, so it follows
 * whichever portal (and light/dark theme) renders it. No date library — plain
 * UTC date math.
 */
export function DateRangePicker({
  from,
  to,
  todayIso,
  locale,
  triggerLabel,
  active,
  applyLabel,
  clearLabel,
  onApply,
  onClear,
}: DateRangePickerProps): ReactElement {
  const [open, setOpen] = useState(false);
  const [start, setStart] = useState<string | null>(from);
  const [end, setEnd] = useState<string | null>(to);
  const [base, setBase] = useState(() => {
    const anchor = to ? new Date(`${to}T00:00:00.000Z`) : new Date();
    return { year: anchor.getUTCFullYear(), month: anchor.getUTCMonth() };
  });
  const boxRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(
    null,
  );

  // Position the panel with fixed viewport coordinates measured from the
  // trigger, then clamp it inside the viewport. Anchoring to an inline edge of
  // the (narrow) trigger container let the wide panel spill off-screen — in RTL
  // it overflowed the right edge on mobile. Measuring + clamping keeps the full
  // panel on-screen in both directions and at any width.
  useEffect(() => {
    if (!open) return;
    const reposition = (): void => {
      const el = triggerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const margin = 8;
      const width = Math.min(window.innerWidth * 0.92, 620);
      // Align the panel's inline-end edge to the trigger, then clamp so it never
      // crosses either viewport edge.
      let left = rect.right - width;
      left = Math.min(
        Math.max(margin, left),
        window.innerWidth - width - margin,
      );
      setCoords({ top: rect.bottom + margin, left });
    };
    reposition();
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    return () => {
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
    };
  }, [open]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent): void => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const pick = (day: string): void => {
    // No start yet, or a full range already picked → begin a new range.
    if (!start || (start && end)) {
      setStart(day);
      setEnd(null);
      return;
    }
    if (day >= start) {
      setEnd(day);
    } else {
      setStart(day);
    }
  };

  const shiftMonth = (delta: number): void =>
    setBase((b) => {
      const d = new Date(Date.UTC(b.year, b.month + delta, 1));
      return { year: d.getUTCFullYear(), month: d.getUTCMonth() };
    });

  const apply = (): void => {
    if (start) {
      onApply(start, end ?? start);
      setOpen(false);
    }
  };

  const nextMonth = new Date(Date.UTC(base.year, base.month + 1, 1));

  return (
    <div className="relative" ref={boxRef}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 rounded-[10px] border px-3 py-1.5 text-[12.5px]"
        style={{
          borderColor: active ? "var(--accent-500)" : "var(--card-br)",
          color: active ? "var(--text-primary)" : "var(--text-muted)",
        }}
      >
        <svg
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <rect x="3" y="4" width="18" height="18" rx="2" />
          <path d="M16 2v4M8 2v4M3 10h18" />
        </svg>
        <bdi>{triggerLabel}</bdi>
      </button>

      {open && coords ? (
        <div
          className="fixed z-50 flex max-h-[85vh] flex-col gap-3 overflow-auto rounded-[14px] border p-4 shadow-xl"
          style={{
            top: coords.top,
            left: coords.left,
            background: "var(--card)",
            borderColor: "var(--card-br)",
            width: "min(92vw, 620px)",
          }}
        >
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => shiftMonth(-1)}
              className="rounded-[8px] px-2 py-1"
              style={{ color: "var(--text-primary)" }}
              aria-label="prev"
            >
              ‹
            </button>
            <button
              type="button"
              onClick={() => shiftMonth(1)}
              className="rounded-[8px] px-2 py-1"
              style={{ color: "var(--text-primary)" }}
              aria-label="next"
            >
              ›
            </button>
          </div>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <MonthGrid
              year={base.year}
              month={base.month}
              start={start}
              end={end}
              todayIso={todayIso}
              onPick={pick}
              locale={locale}
            />
            <MonthGrid
              year={nextMonth.getUTCFullYear()}
              month={nextMonth.getUTCMonth()}
              start={start}
              end={end}
              todayIso={todayIso}
              onPick={pick}
              locale={locale}
            />
          </div>
          <div
            className="flex items-center justify-between border-t pt-3"
            style={{ borderColor: "var(--card-br)" }}
          >
            <button
              type="button"
              onClick={() => {
                setStart(null);
                setEnd(null);
                onClear();
                setOpen(false);
              }}
              className="text-[12.5px] font-semibold"
              style={{ color: "var(--text-muted)" }}
            >
              {clearLabel}
            </button>
            <button
              type="button"
              onClick={apply}
              disabled={!start}
              className="rounded-[10px] px-5 py-2 text-[13px] font-semibold"
              style={{
                background: "var(--accent-500)",
                color: "var(--on-accent)",
                opacity: start ? 1 : 0.5,
              }}
            >
              {applyLabel}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
